'use client';

import { useRouter } from 'next/navigation';
import { useContext, useState } from 'react';

import {
  NO_SEARCH_RESULT_MESSAGE,
  NO_SEARCH_RESULT_TITLE,
} from '@/app/(routes)/listing/_components/page-content/constants';
import {
  NoSearchResult,
} from '@/app/(routes)/listing/_components/page-content/no-search-result';
import {
  ResultPerPageDropdown,
} from '@/app/(routes)/listing/_components/page-content/result-per-page-dropdown'; // @suppress longLineCheck
import {
  createQueryString,
} from '@/app/(routes)/listing/_components/page-content/utils';
import type { OnSearchData } from '@/app/(routes)/listing/types';
import { NewCarBreadcrumb as Breadcrumb } from '@/components/breadcrumb';
import { DYAds } from '@/components/dy-ads';
import { ImageWithFallback } from '@/components/image-with-fallback';
import { Pagination } from '@/components/pagination';
import type {
  FilterChoice,
  FilterRange,
  FilterSelect,
  FilterValue,
} from '@/components/search-filter/filter-label-list/types';
import {
  isFilterCheckbox,
} from '@/components/search-filter/filter-label-list/utils';
import type {
  CheckboxValue,
} from '@/components/search-filter/filter-modal/input/checkbox-group/types';
import type { Category } from '@/components/search-filter/filter-modal/types';
import { newCarListingLeaderboard } from '@/configs/dynamic-yields/ads/desktop';
import {
  AUTHORISED_DISTRIBUTORS,
  PARALLEL_IMPORTERS,
} from '@/constants/global';
import { AppContext } from '@/context/app';
import { joinSgcmSite } from '@/utils/url/join/public';

import { breadcrumbList } from './configs';
import {
  PAGE_TITLE_NEW_CAR_PAST_CAR,
  RESULT_PER_PAGE_NUM_LIMIT,
} from './constants';
import { SearchFilterSection } from './search-filter-section';
import styles from './styles.module.scss';
import type { Component } from './types';

export const PageContent: Component = ({
  brandList,
  pastCarList,
  numOfTotal,
  currentPage,
  lastPage,
  defaultSearchData,
}) => {
  const router = useRouter();
  const appContextData = useContext(AppContext);
  const [
    resultPerPage,
    setResultPerPage,
  ] = useState(defaultSearchData.query.limit ?? RESULT_PER_PAGE_NUM_LIMIT);

  const handleSubmit = (
    searchValue: string,
    _carMakeValue: string,
    selectValues: Record<string, FilterSelect>,
    _rangeValues: Record<string, FilterRange>,
    _choiceValues: Record<string, FilterChoice>,
    checkboxValues: Record<string, Category<'checkbox'>
    & { values: Record<string, CheckboxValue>; }>,
    availableYear: string,
  ) => {
    appContextData?.setIsFilterModalOpen(false);
    const searchData: OnSearchData = {
      query: {
        q: searchValue || undefined,
        page: undefined,
        year: availableYear || undefined,
      },
    };

    Object.values(checkboxValues).forEach((checkbox) => {
      if (checkbox.name === 'Vehicle Type') {
        searchData.query.vehicleType = Object.values(checkbox.values)
          .filter(v => v.checked)
          .map(v => v.value)
          .join(',') || undefined;
      }
    });

    Object.values(selectValues).forEach((v) => {
      switch (v.name) {
        case 'Dealer Type':
          if (v.optionValue.value === AUTHORISED_DISTRIBUTORS
            || v.optionValue.value === PARALLEL_IMPORTERS) {
            searchData.query.dealerType = v.optionValue.value;
          }
          break;
        default:
          break;
      }
    });

    const search = createQueryString(searchData);

    router.push(`/past-cars?${search}`);
  };

  const handleLabelRemoveAll = async () => {
    const searchData: OnSearchData = {
      query: {
        q: defaultSearchData.query.q,
        year: undefined,
        page: undefined,
      },
    };
    const search = createQueryString(searchData);

    router.push(`/past-cars?${search}`);
  };

  const handleLabelRemove = async (index: number, value: FilterValue) => {
    const searchData: OnSearchData = {
      query: {
        ...defaultSearchData.query,
      },
    };

    switch (value.name) {
      case 'Available Year':
        delete searchData.query.year;
        break;
      case 'Dealer Type':
        delete searchData.query.dealerType;
        break;
      case 'Vehicle Type':
        if (isFilterCheckbox(value)) {
          if (searchData.query.vehicleType?.includes(',')) {
            const vehicleType = searchData.query.vehicleType
              .split(',')
              .filter(v => value.checkboxValue !== v)
              .join(',');

            searchData.query.vehicleType = vehicleType;
          } else {
            delete searchData.query.vehicleType;
          }
        }
        break;
      default:
        break;
    }

    const search = createQueryString(searchData);

    router.push(`/past-cars?${search}`);
  };

  const handleFilterClick = () => {
    appContextData?.setIsFilterModalOpen(true);
  };

  const handlePageChange = async (page: number) => {
    const searchData = { query: { ...defaultSearchData.query, page } };
    const search = createQueryString(searchData);

    router.push(`/past-cars?${search}`);
  };

  const handleResultPerPageChange = async (value: number) => {
    setResultPerPage(value);

    const search = createQueryString({
      query: {
        ...defaultSearchData.query,
        page: undefined,
        limit: Number(value),
      },
    });

    router.push(`/past-cars?${search}`);
  };

  return (
    <main
      className={styles.containerPageRoot}>
      <div className={styles.pageContainerRoot}>
        <div className={styles.containerLeaderboard}>
          <DYAds
            smartObjectId={newCarListingLeaderboard.smartObjectId}
            target={newCarListingLeaderboard.target} />
        </div>
        <div className={styles.containerBreadcrumb}>
          <Breadcrumb
            list={breadcrumbList} />
        </div>
        <div className={styles.containerTitle}>
          <h1 className={styles.textTitle}>
            {PAGE_TITLE_NEW_CAR_PAST_CAR}
          </h1>
        </div>
        <div
          className={styles.containerSearchFilter}>
          <SearchFilterSection
            autocompleteType="pastcars"
            brandList={brandList}
            defaultSearchData={defaultSearchData}
            isFilterModalOpen={appContextData?.isFilterModalOpen ?? false}
            simpleMode
            onFilterClick={handleFilterClick}
            onFilterModalClose={
              () => appContextData?.setIsFilterModalOpen(false)
            }
            onLabelRemove={handleLabelRemove}
            onLabelRemoveAll={handleLabelRemoveAll}
            onModalSubmit={handleSubmit}
            onSubmit={handleSubmit} />
        </div>
        <div className={styles.textDesc}>
          There are
          {' '}
          <span className={styles.textSemiboldDesc}>
            {numOfTotal}
            {' '}
            Past New Cars for Sale
          </span>
          {' '}
          no longer being sold by local distributors
        </div>
        <div className={styles.containerContent}>
          {pastCarList.length === 0 && (
            <NoSearchResult
              message={NO_SEARCH_RESULT_MESSAGE}
              pastCarList={null}
              redirectRoute="/past-cars"
              title={NO_SEARCH_RESULT_TITLE}
              isSearchClearable />
          )}
          <div className={styles.containerPastCars}>
            {pastCarList?.map(({
              id, make, model, launchedYear, endedYear, thumbnailUrl,
            }) => {
              const fullName = `${make} ${model}`;
              const yearRange = `${launchedYear}-${endedYear}`;

              return (
                <div
                  key={`pastCar-${id}`}
                  className={styles.containerModel}>
                  <a
                    className={styles.containerVehicleImage}
                    href={`${joinSgcmSite(`/new_cars/newcars_overview.php?CarCode=${id}`)}`}
                    rel="noreferrer noopener">
                    <ImageWithFallback
                      alt={`${make} ${model} image`}
                      className={styles.imageThumbnail}
                      height={144}
                      src={thumbnailUrl}
                      width={216} />
                  </a>
                  <a
                    className={styles.textPastCarTitle}
                    href={`${joinSgcmSite(`/new_cars/newcars_overview.php?CarCode=${id}`)}`}
                    rel="noreferrer noopener">
                    <div className={styles.textModelName}>
                      {fullName}
                    </div>
                    <div className={styles.textRangeYear}>
                      {yearRange}
                    </div>
                  </a>
                </div>
              );
            })}
          </div>
        </div>
        {pastCarList.length !== 0
        && (
          <div className={styles.containerPagination}>
            <div className={styles.pagination}>
              {currentPage && lastPage && (
                <Pagination
                  totalPage={lastPage}
                  value={currentPage}
                  onPageChange={handlePageChange} />
              )}
            </div>
            <div className={styles.resultPerPageDropdown}>
              <ResultPerPageDropdown
                value={resultPerPage}
                onChange={handleResultPerPageChange} />
              <div className={styles.textResultAndPage}>results/page</div>
            </div>
          </div>
        )}
      </div>
    </main>
  );
};
